import React, { ComponentProps } from 'react';
import { DropdownIcon, MenuButton } from './menu';
import { AnyMenuItem } from './menu_model';

export function SelectButton({
  menu: menu,
  searchPlaceholder,
  defaultLabel = 'Select',
  disableWheel,
  ...passthrough
}: {
  /* The checked item will be used as the button label */
  menu: (AnyMenuItem & { type: 'item' })[];
  searchPlaceholder?: string;
  defaultLabel?: string;
  disableWheel?: boolean;
} & Pick<
  ComponentProps<typeof MenuButton>,
  | 'size'
  | 'weight'
  | 'onClose'
  | 'loading'
  | 'disabled'
  | 'tooltip'
  | 'fullWidth'
>) {
  const selectedIndex = menu.findIndex(
    (item) => item.checked && item.type === 'item',
  );
  const selected = menu[selectedIndex];

  const handleWheel = useButtonWheelBehaviour((direction) => {
    let delta = direction;
    while (Math.abs(delta) < menu.length) {
      const nextIndex = (selectedIndex + delta + menu.length * 2) % menu.length;
      const nextItem = menu[nextIndex];
      if (nextItem.disabled) {
        delta += direction;
      } else {
        nextItem.onClick?.();
        break;
      }
    }
  });

  return (
    <MenuButton
      variant="select"
      menu={menu}
      onWheel={disableWheel ? undefined : handleWheel}
      label={selected?.label || defaultLabel}
      subLabel={selected?.subLabel}
      icon={selected?.icon}
      iconRight={<DropdownIcon isSubtle={false} />}
      xalign="left"
      search={React.useMemo(
        () =>
          searchPlaceholder ? { placeholder: searchPlaceholder } : 'hidden',
        [searchPlaceholder],
      )}
      {...passthrough}
    />
  );
}

export function useButtonWheelBehaviour(
  handle: (dir: 1 | -1) => void,
): React.WheelEventHandler {
  const wheelAccumulator = React.useRef(0);
  return React.useCallback(
    (e: React.WheelEvent) => {
      if (isAnyParentScrollable(e.target)) {
        return;
      }
      e.stopPropagation();
      wheelAccumulator.current += e.deltaY;
      if (Math.abs(wheelAccumulator.current) < 100) {
        return;
      }
      const direction = wheelAccumulator.current > 0 ? 1 : -1;
      wheelAccumulator.current = 0;
      handle(direction);
    },
    [handle],
  );
}

function isAnyParentScrollable(target: EventTarget): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.scrollHeight > target.clientHeight + 1) {
    return true;
  }
  if (!target.parentElement) {
    return false;
  }
  return isAnyParentScrollable(target.parentElement);
}
